  <template>
  <div
    data-testid="task-gl-selection"
    :class="{ 'task-gl-selection--assign-accounts': isAssignAccountsVariant }"
  >
    <!-- Figma Assign Accounts step (Add Task AR) -->
    <template v-if="isAssignAccountsVariant">
      <div class="assign-accounts" data-testid="task-gl-selection__assign-accounts">
        <div class="assign-accounts__selected" data-testid="task-gl-selection__selected-wrapper">
          <h5 class="assign-accounts__title ma-0 mb-1" data-testid="task-gl-selection__selected-title">
            Assign Accounts
          </h5>
          <div
            v-if="selectedAccountChips.length"
            class="assign-accounts__chips d-flex flex-wrap"
            data-testid="task-gl-selection__selected-chips"
          >
            <v-chip
              v-for="chip in selectedAccountChips"
              :key="chip.id"
              close
              outlined
              class="assign-accounts__chip"
              :disabled="isUserEditable"
              :data-testid="`task-gl-selection__selected-chip-${chip.id}`"
              @click:close="removeSelectedGl(chip.id)"
            >
              <span class="assign-accounts__chip-text">{{ chip.label }}</span>
            </v-chip>
          </div>
        </div>

        <div class="assign-accounts__picker d-flex flex-column gap-2" data-testid="task-gl-selection__account-picker">
          <p
            v-if="showAccountsHint"
            class="assign-accounts__hint ma-0"
            data-testid="task-gl-selection__hint"
          >
            {{ accountsHint }}
          </p>

          <v-text-field
            v-model="searchcustomfilterLocal"
            dense
            outlined
            hide-details
            clearable
            placeholder="Search by account number or name"
            class="assign-accounts__search"
            :disabled="isUserEditable"
            data-testid="task-gl-selection__search"
            @input="debouncedTbSearch"
            @click:clear="onSearchClear"
          >
            <template v-slot:prepend-inner>
              <img
                src="/img/icons/fbc-icons/SearchIcon.svg"
                alt=""
                class="assign-accounts__search-icon"
                data-testid="task-gl-selection__search-icon"
              />
            </template>
          </v-text-field>
          <div
            class="assign-accounts__table-wrap"
            ref="glScrollContainer"
            @scroll="onGlScroll"
            data-testid="task-gl-selection__menu-scroll"
          >
            <v-skeleton-loader
              v-if="showTableSkeleton"
              type="table-heading, table-tbody, table-tbody, table-tbody"
              class="assign-accounts__skeleton ma-2"
              data-testid="task-gl-selection__skeleton"
            />
            <template v-else>
              <lb-datatableaction
                ref="assignAccountsTable"
                :headers="legacyGlHeaders"
                :tabledata="filteredgllist"
                :displayed-row-count="filteredgllist.length"
                :itemsperpage="filteredgllist.length || 50"
                :bulk-actions="true"
                :show-toolbar="false"
                :show-clear-filter="false"
                :is-data-selected="assignAccountsIndeterminate"
                :selectfilter="{}"
                :page_details="{}"
                :empty-title="emptyAccountsMessage"
                :empty-subtitle="''"
                header-bg-class="fbc-table-header-bg"
                data-testid="task-gl-selection__table"
                @select-all-change="onAssignSelectAll"
              >
                <template #tbody="{ headers }">
                  <tr
                    v-for="(v, k) in filteredgllist"
                    :key="rowSelectionKey(v, k)"
                    :class="{ 'assign-accounts__row--selected': isRowSelected(v) }"
                    :data-testid="`task-gl-selection__row-${k}`"
                    @click="!isUserEditable && toggleAssignAccount(v)"
                  >
                    <td
                      class="assign-accounts__check-col"
                      :data-testid="`task-gl-selection__cell-select-${k}`"
                      @click.stop
                    >
                      <v-checkbox
                        class="ma-0 pa-0 assign-accounts__checkbox"
                        dense
                        hide-details
                        :ripple="false"
                        color="primary"
                        :input-value="isRowSelected(v)"
                        :disabled="isUserEditable"
                        :data-testid="`task-gl-selection__row-checkbox-${k}`"
                        @change="toggleAssignAccount(v, $event)"
                      />
                    </td>
                    <td
                      v-for="header in visibleGlHeaders(headers)"
                      :key="header.value"
                      :class="glCellClass(header.value)"
                      :data-testid="`task-gl-selection__cell-${header.value}-${k}`"
                    >
                      <template v-if="header.value === 'ref'">
                        <div class="d-flex align-center">
                          <OverflowList
                            :items="glReferenceLabels(v.reference_detail)"
                            :visible-count="3"
                            empty-text=""
                            position="top"
                            fixed
                            content-class="task-gl-selection-fixed-tooltip"
                            :data-testid="`task-gl-selection__cell-ref-more-${k}`"
                          >
                            <template #primary="{ item, index }">
                              <v-chip
                                label
                                small
                                class="lightpurple caccounting--text mx-1"
                                :data-testid="`task-gl-selection__cell-ref-chip-${k}-${index}`"
                              ><span>{{ item }}</span></v-chip>
                            </template>
                          </OverflowList>
                        </div>
                      </template>
                      <template v-else-if="header.value === 'location'">
                        <div
                          v-for="(vv, kk) in v.location_detail || []"
                          :key="kk"
                          :data-testid="`task-gl-selection__cell-location-item-${k}-${kk}`"
                        >
                          <span class="ml-2" :data-testid="`task-gl-selection__cell-location-name-${k}-${kk}`">{{ vv.name }}</span>
                        </div>
                      </template>
                      <template v-else>
                        {{ formatGlCellValue(v, header.value) }}
                      </template>
                    </td>
                  </tr>
                </template>
              </lb-datatableaction>
              <div
                v-if="tbLoadingState && filteredgllist.length > 0"
                class="d-flex justify-center pa-2"
                data-testid="task-gl-selection__loading-more"
              >
                <v-progress-circular indeterminate size="24" color="primary" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- Legacy dropdown picker (edit task / other flows) -->
    <template v-else>
    <v-col cols="12" v-if="selectglsLocal && selectglsLocal.length > 0" data-testid="task-gl-selection__selected-wrapper">
      <span class="font-size-lg font-weight-semibold" data-testid="task-gl-selection__selected-title">
        Selected Account
      </span>
      <br />
      <div class="d-flex align-center justify-start flex-wrap " data-testid="task-gl-selection__selected-chips">
        <v-chip
          v-for="chip in selectedAccountChips"
          :key="chip.id"
          class="caccounting--text lightpurple mt-1 mx-1 d-flex"
          close
          :disabled="isUserEditable"
          @click:close="removeSelectedGl(chip.id)"
          :data-testid="`task-gl-selection__selected-chip-${chip.id}`"
        >
          <span>{{ chip.label }}</span>
        </v-chip>
      </div>
    </v-col>
    <v-col cols="12" style="height: 10vh" v-if="addeditdata.location && addeditdata.task_type === $constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION" data-testid="task-gl-selection__account-picker">
      <v-menu
        :close-on-content-click="false"
        offset-y
        transition="slide-y-transition"
        ref="accountMenu"
        data-testid="task-gl-selection__menu"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            v-bind="attrs"
            v-on="on"
            class="all-width align-center border-on justify-space-between select-account__no"
            :disabled="isUserEditable"
            data-testid="task-gl-selection__account-menu-btn"
            ><span data-testid="task-gl-selection__account-menu-btn-label" >Select Account Number</span
            ><v-icon data-testid="task-gl-selection__account-menu-btn-icon">mdi-chevron-down</v-icon></v-btn
          >
        </template>

        <v-list
          class="ma-0 d-flex flex-column align-start justify-start all-width pa-2"
          data-testid="task-gl-selection__menu-list"
        >
          <div
            class=""
            style="
              max-height: 45vh;
              min-width: 100%;
              overflow: auto;
              z-index: 13 !important;
              width: 20vh;
            "
            ref="glScrollContainer"
            @scroll="onGlScroll"
            data-testid="task-gl-selection__menu-scroll"
          >
            <lb-string
              label="Search"
              class="all-width"
              v-model="searchcustomfilterLocal"
              hidedetails
              @input="debouncedTbSearch"
              data-testid="task-gl-selection__search"
            />
            <v-skeleton-loader
              v-if="showTableSkeleton"
              type="table-heading, table-tbody, table-tbody"
              class="mt-2"
              data-testid="task-gl-selection__skeleton"
            />
            <template v-else>
            <lb-datatableaction
              :headers="legacyGlHeaders"
              :tabledata="filteredgllist"
              :displayed-row-count="filteredgllist.length"
              :itemsperpage="filteredgllist.length || 50"
              :bulk-actions="true"
              :show-toolbar="false"
              :show-clear-filter="false"
              :selectfilter="{}"
              :page_details="{}"
              :empty-title="emptyAccountsMessage"
              :empty-subtitle="''"
              header-bg-class="fbc-table-header-bg"
              class="all-width mt-2"
              data-testid="task-gl-selection__table"
              @select-all-change="toggleSelectAllGl"
            >
              <template #tbody>
                <tr
                  v-for="(v, k) in filteredgllist"
                  :key="rowSelectionKey(v, k)"
                  :data-testid="`task-gl-selection__row-${k}`"
                  class="taskgl-selection__row"
                >
                  <td :data-testid="`task-gl-selection__cell-select-${k}`">
                    <v-checkbox
                      class="mt-0"
                      dense
                      label=""
                      :value="rowSelectionKey(v, k)"
                      v-model="tempGlSelection"
                      multiple
                      hide-details
                      :data-testid="`task-gl-selection__row-checkbox-${k}`"
                    />
                  </td>
                  <td :data-testid="`task-gl-selection__cell-account-no-${k}`">{{ v.glcode }}</td>
                  <td :data-testid="`task-gl-selection__cell-account-name-${k}`">{{ v.name || '—' }}</td>
                  <td :data-testid="`task-gl-selection__cell-ref-${k}`">
                    <div class="d-flex align-center min-height">
                      <OverflowList
                        :items="glReferenceLabels(v.reference_detail)"
                        :visible-count="3"
                        empty-text=""
                        position="top"
                        fixed
                        content-class="task-gl-selection-fixed-tooltip"
                        :data-testid="`task-gl-selection__cell-ref-more-${k}`"
                      >
                        <template #primary="{ item, index }">
                          <v-chip
                            label
                            small
                            class="lightpurple caccounting--text mx-1"
                            :data-testid="`task-gl-selection__cell-ref-chip-${k}-${index}`"
                          ><span>{{ item }}</span></v-chip>
                        </template>
                      </OverflowList>
                    </div>
                  </td>
                  <td :data-testid="`task-gl-selection__cell-location-${k}`">
                    <div
                      v-for="(vv, kk) in v.location_detail || []"
                      :key="kk"
                      :data-testid="`task-gl-selection__cell-location-item-${k}-${kk}`"
                    >
                      <span class="ml-2" :data-testid="`task-gl-selection__cell-location-name-${k}-${kk}`">{{ vv.name }}</span>
                    </div>
                  </td>
                  <td :data-testid="`task-gl-selection__cell-type-${k}`">{{ v.type || '—' }}</td>
                  <td :data-testid="`task-gl-selection__cell-opening-amount-${k}`">{{ v.opening_amount }}</td>
                  <td :data-testid="`task-gl-selection__cell-transaction-${k}`">{{ v.transaction }}</td>
                  <td :data-testid="`task-gl-selection__cell-audit-entries-${k}`">{{ v.audit_entries }}</td>
                  <td :data-testid="`task-gl-selection__cell-closing-amount-${k}`">{{ v.closing_amount }}</td>
                  <td :data-testid="`task-gl-selection__cell-category-${k}`">{{ v.category || '—' }}</td>
                  <td :data-testid="`task-gl-selection__cell-subcategory-${k}`">{{ v.subcategory || '—' }}</td>
                </tr>
              </template>
            </lb-datatableaction>
            <div v-if="tbLoadingState && filteredgllist.length > 0" class="d-flex justify-center pa-2" data-testid="task-gl-selection__loading-more">
              <v-progress-circular indeterminate size="24" color="primary" />
            </div>
            <div v-if="tbFullyLoadedState && filteredgllist.length > 0" class="text-center grey--text caption pa-1" data-testid="task-gl-selection__all-loaded">
              All records loaded
            </div>
            </template>
          </div>
          <div class="d-flex align-center all-width justify-end mt-2" data-testid="task-gl-selection__select-actions">
            <v-btn
              class="ml-2 white--text"
              color="primary"
              @click="
                addSelectedGls({ skipTrialBalance: true });
                $refs.accountMenu.isActive = false;
              "
              data-testid="task-gl-selection__select-btn"
              ><span>Select</span></v-btn
            >
          </div>
        </v-list>
      </v-menu>
    </v-col>
    <v-card-actions class="pt-10" v-if="!isUserEditable && !hideUpdateButton" data-testid="task-gl-selection__actions">
      <span class="caption red--text" data-testid="task-gl-selection__required-note">*Required</span>
      <v-spacer></v-spacer>
      <v-btn color="primary" :loading="addeditloading" @click="addEditChecklistItem()" data-testid="task-gl-selection__update-btn">
        <span data-testid="task-gl-selection__update-btn-label">Update</span>
      </v-btn>
    </v-card-actions>
    </template>
  </div>
</template>

<script>
import {
  ASSIGN_ACCOUNTS_HINT,
  getGlPickerTableHeaders,
  TB_GL_SEARCH_FIELDS,
  GL_PICKER_HEADER_VALUES,
  GL_PICKER_NOWRAP_COLUMNS,
  GL_PICKER_TEXT_FALLBACK_COLUMNS,
} from '../../../utils/constants.js';
import { getCustomColumns, shouldSkipWatcherChange } from '../../../utils/utils.js';
import {
  buildLocationTypeById,
  isConsolidatedLocationId,
} from '../../../utils/locationMasterType';
import {
  buildSelectedTbTotals,
  indexTbRecordsById,
  missingTbRecordIds,
  selectionKeysFromTask,
  selectedTbLinkIds,
  tbLocationDisplayName,
  tbRecordId,
  tbSelectionKey,
} from '../../../utils/tbGlLocationValidation';
import './TaskGLSelection.scss';
import { OverflowList } from '@/modules/financialclose/components';

export default {
  name: 'TaskGLSelection',
  components: {
    OverflowList,
  },
  props: {
    addeditdata: { type: Object, default: () => ({}) },
    addeditdataerror: { type: Object, default: () => ({}) },
    addeditdialog: { type: Boolean, default: false },
    customColumns: { type: Array, default: () => [] },
    selectgls: { type: Array, default: () => [] },
    searchcustomfilter: { type: String, default: '' },
    hideUpdateButton: { type: Boolean, default: false },
    isUserEditable: { type: Boolean, default: false },
    /** Figma Add Task AR step — inline Assign Accounts table (not dropdown). */
    variant: { type: String, default: 'default' },
    /** Location master rows with `type` (from cycle formdata / locationList). */
    locations: { type: Array, default: () => [] },
  },
  data() {
    return {
      addeditloading: false,
      selectglsLocal: [],
      tempGlSelection: [],
      searchcustomfilterLocal: this.searchcustomfilter,
      tbSearchTimer: null,
      tbSearchReady: false,
      lastSearchedValue: null,
      /** When true, newly loaded infinite-scroll rows stay selected. */
      assignSelectAllActive: false,
      /** TB rows fetched by id so selected accounts survive partial picker pages. */
      resolvedTbById: {},
      selectionSyncPromise: null,
      selectionSyncGeneration: 0,
    };
  },
  mounted() {
    this.hydrateSelectionFromTask(this.addeditdata);
    this.$emit('update:selectgls', [...this.selectglsLocal]);
    this.loadTbFirstPage();
    this.addSelectedGls();
    this.$nextTick(() => { this.tbSearchReady = true; });
  },
  beforeDestroy() {
    if (this.tbSearchTimer) {
      clearTimeout(this.tbSearchTimer);
      this.tbSearchTimer = null;
    }
  },
  watch: {
    addeditdialog(val) {
      if (val) {
        this.hydrateSelectionFromTask(this.addeditdata);
        this.addSelectedGls();
      }
    },
    searchcustomfilterLocal() {
      this.tempGlSelection = (this.tempGlSelection || []).map(String);
    },
    'addeditdata.task_type'(newType) {
      if (newType === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL) {
        this.selectglsLocal = [];
        this.$emit('update:selectgls', this.selectglsLocal);
      }
    },
    'addeditdata.location'(newLocation, oldLocation) {
      if (shouldSkipWatcherChange(newLocation, oldLocation)) return;
      this.clearGlSelection();
      if (newLocation) {
        this.loadTbFirstPage();
      } else {
        this.$store.commit('tasks/RESET_TB_STATE');
      }
    },
    selectgls(newVal) {
      if (Array.isArray(newVal) && newVal.length > 0) {
        this.selectglsLocal = newVal.map(String);
        this.tempGlSelection = [...this.selectglsLocal];
      } else if (!newVal || (Array.isArray(newVal) && newVal.length === 0)) {
        this.selectglsLocal = [];
        this.tempGlSelection = [];
      }
    },
    selectAllgl(val) {
      this.$nextTick(() => {
        if (this.$refs.assignAccountsTable) {
          this.$refs.assignAccountsTable.selectAll = !!val;
        }
      });
    },
    addeditdata(newData, oldData) {
      if (!newData) return;
      const newId = newData._id;
      const oldId = oldData && oldData._id;
      if (newId && newId === oldId) return;
      this.hydrateSelectionFromTask(newData);
      this.$emit('update:selectgls', [...this.selectglsLocal]);
      this.addSelectedGls();
    },
  },
  methods: {
    hydrateSelectionFromTask(task) {
      const keys = selectionKeysFromTask(task);
      this.selectglsLocal = keys;
      this.tempGlSelection = [...keys];
    },
    rowSelectionKey(item, fallbackIndex) {
      return tbSelectionKey(item) || (fallbackIndex != null ? String(fallbackIndex) : '');
    },
    clearGlSelection() {
      this.selectglsLocal = [];
      this.tempGlSelection = [];
      this.assignSelectAllActive = false;
      this.resolvedTbById = {};
      this.searchcustomfilterLocal = '';
      this.lastSearchedValue = null;
      this.$set(this.addeditdata, '__tblinks', []);
      this.$set(this.addeditdata, '__tbtotals', { total: 0, items: [] });
      this.$emit('update:selectgls', []);
    },
    async loadTbFirstPage() {
      const projectId = this.addeditdata.project || this.$route.params.id || this.$route.params.projectId;
      if (!projectId) return;
      this.lastSearchedValue = null;
      this.assignSelectAllActive = false;
      // Pass the selected location id as today. Consolidated type is resolved
      // on supernova getglaccounts — do not strip it here.
      await this.$store.dispatch('tasks/fetchTbPage', {
        projectId,
        page: 1,
        limit: 50,
        location: this.addeditdata.location || null,
        search: null,
        searchFields: TB_GL_SEARCH_FIELDS,
        append: false,
      });
    },
    onGlScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        this.loadNextTbPage();
      }
    },
    async loadNextTbPage({ skipSelectAllApply = false } = {}) {
      const tbLoading = this.$store.getters['tasks/isTbLoading'];
      const tbFullyLoaded = this.$store.getters['tasks/isTbFullyLoaded'];
      if (tbLoading || tbFullyLoaded) return;
      const tbPagination = this.$store.getters['tasks/getTbPagination'] || {};
      const currentPage = tbPagination.page || 1;
      const projectId = this.addeditdata.project || this.$route.params.id || this.$route.params.projectId;
      if (!projectId) return;
      const searchVal = (this.searchcustomfilterLocal || '').trim() || null;
      await this.$store.dispatch('tasks/fetchTbPage', {
        projectId,
        page: currentPage + 1,
        limit: 50,
        location: this.addeditdata.location || null,
        search: searchVal,
        searchFields: TB_GL_SEARCH_FIELDS,
        append: true,
      });
      if (this.assignSelectAllActive && !skipSelectAllApply) {
        this.applyFilteredSelection(true);
      }
    },
    /** Load remaining TB pages so Select All can cover the full filtered set. */
    async loadAllRemainingTbPages() {
      const projectId = this.addeditdata.project || this.$route.params.id || this.$route.params.projectId;
      if (!projectId) return;
      const tbPagination = this.$store.getters['tasks/getTbPagination'] || {};
      const currentPage = tbPagination.page || 1;
      const totalPages = tbPagination.totalPages || 0;
      if (!totalPages || currentPage >= totalPages) return;
      const searchVal = (this.searchcustomfilterLocal || '').trim() || null;
      for (let page = currentPage + 1; page <= totalPages; page += 1) {
        if (this.$store.getters['tasks/isTbFullyLoaded']) break;
        // eslint-disable-next-line no-await-in-loop
        await this.$store.dispatch('tasks/fetchTbPage', {
          projectId,
          page,
          limit: 50,
          location: this.addeditdata.location || null,
          search: searchVal,
          searchFields: TB_GL_SEARCH_FIELDS,
          append: true,
        });
      }
    },
    applyFilteredSelection(shouldSelect) {
      const filteredIds = this.filteredgllist.map((row) => tbSelectionKey(row)).filter(Boolean);
      let next;
      if (shouldSelect) {
        next = Array.from(new Set([...this.selectglsLocal.map(String), ...filteredIds]));
      } else {
        next = this.selectglsLocal.map(String).filter((id) => !filteredIds.includes(id));
      }
      this.selectglsLocal = next;
      this.tempGlSelection = [...next];
      this.addSelectedGls();
    },
    onSearchClear() {
      this.searchcustomfilterLocal = '';
      this.assignSelectAllActive = false;
      this.$nextTick(() => this.debouncedTbSearch());
    },
    debouncedTbSearch() {
      if (!this.tbSearchReady) return;
      if (this.tbSearchTimer) clearTimeout(this.tbSearchTimer);
      this.tbSearchTimer = setTimeout(() => {
        const searchVal = (this.searchcustomfilterLocal || '').trim() || null;
        if (searchVal === this.lastSearchedValue) return;
        this.lastSearchedValue = searchVal;
        this.assignSelectAllActive = false;
        this.$emit('update:searchcustomfilter', this.searchcustomfilterLocal || '');
        const projectId = this.addeditdata.project || this.$route.params.id || this.$route.params.projectId;
        if (!projectId) return;
        this.$store.dispatch('tasks/fetchTbPage', {
          projectId,
          page: 1,
          limit: 50,
          location: this.addeditdata.location || null,
          search: searchVal,
          searchFields: TB_GL_SEARCH_FIELDS,
          append: false,
        });
      }, 300);
    },
    removeSelectedGl(selectionKey) {
      if (!selectionKey) return;
      const key = String(selectionKey);
      this.assignSelectAllActive = false;
      const newSelectGls = this.selectglsLocal.map(String).filter((id) => id !== key);
      const newTempGls = this.tempGlSelection.map(String).filter((id) => id !== key);
      this.selectglsLocal = newSelectGls;
      this.tempGlSelection = newTempGls;
      this.addSelectedGls();
    },
    toggleSelectAllGl(val) {
      const shouldSelect = typeof val === 'boolean' ? val : this.selectAllgl;
      const filteredIds = this.filteredgllist.map((row) => tbSelectionKey(row)).filter(Boolean);
      if (shouldSelect) {
        this.tempGlSelection = Array.from(new Set([
          ...this.tempGlSelection.map(String),
          ...filteredIds
        ]));
      } else {
        this.tempGlSelection = this.tempGlSelection.map(String).filter(id => !filteredIds.includes(id));
      }
    },
    isRowSelected(item) {
      const key = tbSelectionKey(item);
      if (!key) return false;
      return this.selectglsLocal.map(String).includes(key);
    },
    visibleGlHeaders(headers) {
      return (headers || []).filter((header) => header && header.visible !== false);
    },
    glCellClass(headerValue) {
      if (GL_PICKER_NOWRAP_COLUMNS.includes(headerValue)) {
        return 'white-space-nowrap';
      }
      return '';
    },
    glReferenceLabels(details) {
      if (!Array.isArray(details)) return [];
      return details.reduce((labels, entry) => {
        const ref = entry && entry.ref != null ? String(entry.ref).trim() : '';
        if (ref) labels.push(ref);
        return labels;
      }, []);
    },
    formatGlCellValue(row, headerValue) {
      if (!row) return '—';
      if (headerValue === GL_PICKER_HEADER_VALUES.GLCODE) return row[GL_PICKER_HEADER_VALUES.GLCODE];
      if (GL_PICKER_TEXT_FALLBACK_COLUMNS.includes(headerValue)) {
        return row[headerValue] || '—';
      }
      const value = row[headerValue];
      return value == null || value === '' ? '' : value;
    },
    /** Immediate select/deselect for Assign Accounts variant (no Select button). */
    toggleAssignAccount(item, checked) {
      if (!item || this.isUserEditable) return;
      const key = tbSelectionKey(item);
      if (!key) return;
      const shouldSelect = typeof checked === 'boolean' ? checked : !this.isRowSelected(item);
      if (!shouldSelect) this.assignSelectAllActive = false;
      let next;
      if (shouldSelect) {
        next = Array.from(new Set([...this.selectglsLocal.map(String), key]));
      } else {
        next = this.selectglsLocal.map(String).filter((id) => id !== key);
      }
      this.selectglsLocal = next;
      this.tempGlSelection = [...next];
      this.addSelectedGls();
    },
    async onAssignSelectAll(val) {
      if (this.isUserEditable) return;
      this.assignSelectAllActive = !!val;
      if (val) {
        // Select currently loaded rows immediately, then fetch the rest.
        this.applyFilteredSelection(true);
        await this.loadAllRemainingTbPages();
        this.applyFilteredSelection(true);
      } else {
        this.applyFilteredSelection(false);
      }
    },
    applySelectedTbPayload() {
      const selectedIds = selectedTbLinkIds(this.selectglsLocal);
      this.selectglsLocal = selectedIds;
      this.$set(this.addeditdata, '__tblinks', [...selectedIds]);
      const { items, total } = buildSelectedTbTotals(selectedIds, this.glItemById);
      this.$set(this.addeditdata, '__tbtotals', { total, items });
      this.$emit('update:selectgls', [...selectedIds]);
    },
    async resolveMissingSelectedTbRows() {
      const missingIds = missingTbRecordIds(this.selectglsLocal, this.glItemById);
      if (!missingIds.length) return;
      const projectId = this.addeditdata.project || this.$route.params.id || this.$route.params.projectId;
      if (!projectId) return;
      const records = await this.$store.dispatch('tasks/fetchTbByIds', {
        projectId,
        ids: missingIds,
      });
      if (!Array.isArray(records) || !records.length) return;
      const next = { ...this.resolvedTbById };
      records.forEach((record) => {
        const id = tbRecordId(record);
        if (id) next[id] = record;
      });
      this.resolvedTbById = next;
    },
    async addSelectedGls() {
      this.selectglsLocal = selectedTbLinkIds(this.tempGlSelection);
      this.applySelectedTbPayload();
      const generation = this.selectionSyncGeneration + 1;
      this.selectionSyncGeneration = generation;
      const pending = this.syncSelectedTbMetadata(generation);
      this.selectionSyncPromise = pending;
      await pending;
    },
    async syncSelectedTbMetadata(generation) {
      try {
        await this.resolveMissingSelectedTbRows();
        if (generation !== this.selectionSyncGeneration) return;
        this.applySelectedTbPayload();
      } finally {
        if (generation === this.selectionSyncGeneration) {
          this.selectionSyncPromise = null;
        }
      }
    },
    async addEditChecklistItem() {
      this.addeditloading = true;
      try {
        if (this.selectionSyncPromise) await this.selectionSyncPromise;
        const selectglsAsIds = (this.addeditdata.__tblinks || []).map(String);
        const payload = {
          addeditdata: { ...this.addeditdata },
          customColumns: this.customColumnsComputed,
          selectglsLocal: selectglsAsIds,
        };
        const result = await this.$store.dispatch('tasks/addEditChecklistItem', payload);
        if (result && result.success) {
          this.$store.commit('sbSuccess', 'Tasklist item updated');
          this.$emit('checklist-item-updated', {
            data: {
              ...result.data,
              _id: this.addeditdata._id,
              __tblinks: selectglsAsIds,
              __tbtotals: this.addeditdata.__tbtotals,
            }
          });
          const checklistId = this.addeditdata.project || this.addeditdata._id;
          await this.$store.dispatch('tasks/getAllTasks', checklistId ? { checklistId } : {});
          this.$emit('refresh');
        } else {
          if (result && result.errorMessage) {
            this.$store.commit('sbError', result.errorMessage);
          } else if (result && result.data && result.data.index0) {
            this.addeditdataerror = result.data.index0;
            this.$store.commit('sbError', 'Validation error');
          } else {
            this.$store.commit('sbError', 'Unknown error!');
          }
        }
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.log(err);
      } finally {
        this.addeditloading = false;
      }
    },
    refreshData() {
      this.$emit('refresh');
    },
  },
  computed: {
    isAssignAccountsVariant() {
      return this.variant === 'assign-accounts';
    },
    cycleLocations() {
      const form = this.$store.getters['tasks/getFormDataV3'] || {};
      const fromMap = Object.values(this.locationMap || {});
      const fromProp = Array.isArray(this.locations) ? this.locations : [];
      return [
        ...fromProp,
        ...fromMap,
        ...(form.locations || []),
        ...(form.actorLocations || []),
      ];
    },
    isConsolidatedLocationSelected() {
      return isConsolidatedLocationId(
        this.addeditdata?.location,
        buildLocationTypeById(this.cycleLocations)
      );
    },
    accountsHint() {
      return this.isConsolidatedLocationSelected
        ? ASSIGN_ACCOUNTS_HINT.CONSOLIDATED
        : ASSIGN_ACCOUNTS_HINT.FILTERED_BY_LOCATION;
    },
    showAccountsHint() {
      return this.tbLoadingState || this.filteredgllist.length > 0;
    },
    emptyAccountsMessage() {
      if (this.searchcustomfilterLocal) return ASSIGN_ACCOUNTS_HINT.EMPTY_SEARCH;
      if (this.isConsolidatedLocationSelected) return ASSIGN_ACCOUNTS_HINT.EMPTY;
      return this.isAssignAccountsVariant
        ? ASSIGN_ACCOUNTS_HINT.EMPTY_LOCATION
        : ASSIGN_ACCOUNTS_HINT.EMPTY;
    },
    showTableSkeleton() {
      return this.tbLoadingState && this.filteredgllist.length === 0;
    },
    assignAccountsIndeterminate() {
      if (!this.isAssignAccountsVariant || !this.filteredgllist.length) return false;
      const selected = this.selectglsLocal.map(String);
      const selectedCount = this.filteredgllist.filter((item) => selected.includes(tbSelectionKey(item))).length;
      return selectedCount > 0 && selectedCount < this.filteredgllist.length;
    },
    legacyGlHeaders() {
      return getGlPickerTableHeaders(this.$store.state.LocationHeader || 'Location');
    },
    glItemById() {
      const savedItems = this.addeditdata?.__tbtotals?.items;
      const computedItems = this.addeditdata?.__computed?.tbtotals?.items;
      return indexTbRecordsById([
        ...(Array.isArray(computedItems) ? computedItems : []),
        ...(Array.isArray(savedItems) ? savedItems : []),
        ...(this.tbComputed || []),
        ...Object.values(this.resolvedTbById || {}),
      ]);
    },
    selectedAccountChips() {
      const byId = this.glItemById;
      return (this.selectglsLocal || []).map((key) => {
        const id = String(key);
        const item = byId[id];
        const glcode = item?.glcode != null ? String(item.glcode) : id;
        const name = item?.name ? String(item.name).trim() : '';
        const location = tbLocationDisplayName(item);
        const base = name ? `${glcode} - ${name}` : glcode;
        return {
          id,
          glcode,
          name,
          label: location ? `${base} (${location})` : base,
        };
      });
    },
    taskListData() {
      return this.$store.getters['tasks/getTaskListData'] || {};
    },
    customColumnsComputed() {
      const checklist = this.taskListData.checklist || [];
      let mergedCustomFields = {};
      for (const item of checklist) {
        if (Array.isArray(item.customfields) && item.customfields.length > 0) {
          for (const fieldObj of item.customfields) {
            if (fieldObj && typeof fieldObj === 'object') {
              Object.assign(mergedCustomFields, fieldObj);
            }
          }
        }
      }
      try {
        return getCustomColumns(mergedCustomFields);
      } catch (e) {
        return [];
      }
    },
    filteredgllist() {
      // Location filtering is done server-side via fetchTbPage `location`.
      // Avoid a second client filter (ObjectId/string mismatches were emptying the table).
      const tbRecords = this.$store.getters['tasks/getTbRecords'] || [];
      return Array.isArray(tbRecords) ? tbRecords : [];
    },
    tbLoadingState() {
      return this.$store.getters['tasks/isTbLoading'] || false;
    },
    tbFullyLoadedState() {
      return this.$store.getters['tasks/isTbFullyLoaded'] || false;
    },
    tbComputed() {
      const tbRecords = this.$store.getters['tasks/getTbRecords'] || [];
      return Array.isArray(tbRecords) ? tbRecords : [];
    },
    locationMap() {
      return this.$store.getters['tasks/getLocationMap'] || {};
    },
    selectAllgl: {
      get() {
        const selectedIds = this.isAssignAccountsVariant
          ? this.selectglsLocal.map(String)
          : this.tempGlSelection.map(String);
        return this.filteredgllist.length > 0 && this.filteredgllist.every(
          (item) => selectedIds.includes(tbSelectionKey(item))
        );
      },
      set(val) {
        const filteredIds = this.filteredgllist.map((row) => tbSelectionKey(row)).filter(Boolean);
        if (val) {
          this.tempGlSelection = Array.from(new Set([
            ...this.tempGlSelection.map(String),
            ...filteredIds
          ]));
        } else {
          this.tempGlSelection = this.tempGlSelection.map(String).filter(id => !filteredIds.includes(id));
        }
      }
    },
  }
}
</script>
